import type { AnyObject } from "yup"
import { api } from "./api"
import { useLangRefetch } from "./utils"
import type { LangWrapHook } from "./types"
import type { WithId, components } from "./generated"

type CustomerGroup = WithId<components["schemas"]["CustomerGroup"]>
type RootGroupClass = WithId<components["schemas"]["RootGroupClass"]>

const rootGroupClassApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getCustomerGroups: builder.query<CustomerGroup[], AnyObject | void>({
			query: (params) => ({
				url: "/customerGroup",
				params: params ?? undefined,
			}),
			providesTags: (result, error) => {
				if (error || !result) return []
				return [
					...result.map((doc) => ({ type: "CustomerGroup" as const, id: doc._id })),
					{ type: "CustomerGroup" as const, id: "LIST" },
					"Loc" as const,
				]
			},
		}),
		getCustomerGroup: builder.query<CustomerGroup, string>({
			query: (id) => ({
				url: `/customerGroup/${id}`,
			}),
			providesTags: (_result, _error, arg) => {
				return [{ type: "CustomerGroup", id: arg }]
			},
		}),
		createCustomerGroup: builder.mutation<CustomerGroup, AnyObject>({
			query: (body) => ({
				url: "/customerGroup",
				method: "POST",
				body,
			}),
			invalidatesTags: () => {
				return [{ type: "CustomerGroup", id: "LIST" }]
			},
		}),
		updateCustomerGroup: builder.mutation<CustomerGroup, { id: string; body: AnyObject }>({
			query: ({ id, body }) => ({
				url: `/customerGroup/${id}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return [
					{ type: "CustomerGroup", id: "LIST" },
					{ type: "CustomerGroup", id: args.id },
				]
			},
		}),
		removeCustomerGroup: builder.mutation<void, string>({
			query: (id) => ({
				url: `/customerGroup/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: () => {
				return [{ type: "CustomerGroup", id: "LIST" }]
			},
		}),
		getRootGroupClasses: builder.query<RootGroupClass[], AnyObject | void>({
			query: (params) => ({
				url: "/rootGroupClass",
				params: params ?? undefined,
			}),
			providesTags: (result, error) => {
				if (error || !result) return []
				return [
					...result.map((doc) => ({ type: "RootGroupClass" as const, id: doc._id })),
					{ type: "RootGroupClass" as const, id: "LIST" },
					"Loc" as const,
				]
			},
		}),
		getRootGroupClass: builder.query<RootGroupClass, { id: string; noLoc?: boolean }>({
			query: ({ id, noLoc }) => ({
				url: `/rootGroupClass/${id}`,
				params: {
					noLoc,
				},
			}),
			providesTags: (_result, _error, arg) => {
				return [{ type: "RootGroupClass", id: arg.id }]
			},
		}),
		createRootGroupClass: builder.mutation<RootGroupClass, AnyObject>({
			query: (body) => ({
				url: "/rootGroupClass",
				method: "POST",
				body,
			}),
			invalidatesTags: () => {
				return [{ type: "RootGroupClass", id: "LIST" }]
			},
		}),
		updateRootGroupClass: builder.mutation<RootGroupClass, { id: string; body: AnyObject }>({
			query: ({ id, body }) => ({
				url: `/rootGroupClass/${id}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return [
					{ type: "RootGroupClass", id: "LIST" },
					{ type: "CustomerGroup", id: "LIST" },
					{ type: "RootGroupClass", id: args.id },
				]
			},
		}),
		removeRootGroupClass: builder.mutation<void, string>({
			query: (id) => ({
				url: `/rootGroupClass/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: () => {
				return [
					{ type: "RootGroupClass", id: "LIST" },
					{ type: "CustomerGroup", id: "LIST" },
				]
			},
		}),
		addRoleMemberRGC: builder.mutation<RootGroupClass, { id: string; body: AnyObject }>({
			query: ({ id, body }) => ({
				url: `/rootGroupClass/${id}/roleMember`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => {
				return [{ type: "RootGroupClass", id: arg.id }]
			},
		}),
		removeRoleMemberRGC: builder.mutation<RootGroupClass, { id: string; profileId: string; body?: AnyObject }>({
			query: ({ id, profileId, body }) => ({
				url: `/rootGroupClass/${id}/roleMember/${profileId}`,
				method: "DELETE",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => {
				return [
					{ type: "RootGroupClass", id: arg.id },
					{ type: "Profile", id: arg.profileId },
				]
			},
		}),
	}),
})

export const {
	useGetCustomerGroupsQuery,
	useGetCustomerGroupQuery,
	useCreateCustomerGroupMutation,
	useUpdateCustomerGroupMutation,
	useRemoveCustomerGroupMutation,
	useGetRootGroupClassesQuery,
	useGetRootGroupClassQuery,
	useCreateRootGroupClassMutation,
	useUpdateRootGroupClassMutation,
	useRemoveRootGroupClassMutation,
	useAddRoleMemberRGCMutation,
	useRemoveRoleMemberRGCMutation,
} = rootGroupClassApi

export const useGetCustomerGroupsQueryL: LangWrapHook<CustomerGroup[]> = (arg, options) =>
	useLangRefetch(useGetCustomerGroupsQuery, arg, options)
export const useGetRootGroupClassesQueryL: LangWrapHook<RootGroupClass[]> = (arg, options) =>
	useLangRefetch(useGetRootGroupClassesQuery, arg, options)
